import { Injectable } from '@angular/core';
import { Cell, TerrainGrid } from '../models/terrain-grid';
import {
  EraseRequest,
  EraseResponse,
  PaintRequest,
  PaintResponse,
  PaintCellsRequest,
  PaintMultipleRequest,
} from '../generated/paint_pb';
import { grpc } from '@improbable-eng/grpc-web';
import { TerrainPaintingService } from '../generated/paint_pb_service';
import { Point } from '../generated/models_pb';
import { IGeoJSONFeatureCollection } from '../interfaces/geo';
import { RequestHeaderService } from './request-header.service';
import { InactivityService } from './inactivity.service';

@Injectable({
  providedIn: 'root',
})
export class PaintService {
  constructor(private requestHeaderService: RequestHeaderService, private inactivityService: InactivityService) {}

  public erase(
    indexes: number[],
    terrainGrid: TerrainGrid
  ): Promise<EraseResponse.AsObject> {
    const request: EraseRequest = new EraseRequest();
    request.setIndexesList(indexes);
    if (!terrainGrid.id) {
      throw Error('Terrain Grid does not have a value for `id` property.');
    }
    request.setSimulationid(terrainGrid.id);

    let response: EraseResponse.AsObject;

    return this.requestHeaderService.getHeaderPromise().then((metadata) => {
      return new Promise((resolve, reject) => {
        this.inactivityService.resetInactivityTimer();
        grpc.invoke(TerrainPaintingService.Erase, {
          request: request,
          host: '',
          metadata: new grpc.Metadata(metadata),
          onMessage: (message: EraseResponse) => {
            response = message.toObject();
          },
          onEnd: (
            code: grpc.Code,
            message: string,
            trailers: grpc.Metadata
          ) => {
            if (code === grpc.Code.OK) {
              resolve(response);
              console.debug(`${indexes.length} agents are removed.`);
            } else {
              console.error('An error occurred', code, message, trailers);
              reject(new Error(`gRPC error: ${code} - ${message}`));
            }
          },
        });
      });
    });
  }

  public paint(
    index: number,
    landslideId: number,
    terrainGrid: TerrainGrid,
    is3x3: boolean
  ): Promise<PaintResponse.AsObject> {
    const request: PaintRequest = new PaintRequest();
    if (!terrainGrid.id) {
      throw Error('Terrain Grid does not have a value for `id` property.');
    }
    request.setSimulationid(terrainGrid.id);
    request.setIndex(index);
    request.setLandslideid(landslideId);
    request.setIs3x3(is3x3);
    let response: PaintResponse.AsObject;

    return this.requestHeaderService.getHeaderPromise().then((metadata) => {
      return new Promise((resolve, reject) => {
        this.inactivityService.resetInactivityTimer();
        grpc.invoke(TerrainPaintingService.Paint, {
          request: request,
          host: '',
          metadata: new grpc.Metadata(metadata),
          onMessage: (message: PaintResponse) => {
            response = message.toObject();
            this.updateTerrainGrid(response, terrainGrid);
          },
          onEnd: (
            code: grpc.Code,
            message: string,
            trailers: grpc.Metadata
          ): any => {
            if (code === grpc.Code.OK) {
              resolve(response); // Resolve the promise when the gRPC call is successful
            } else {
              console.error('An error occurred', code, message, trailers);
              reject(new Error(`gRPC error: ${code} - ${message}`));
            }
          },
        });
      });
    });
    
  }

  public paintThemAll(
    indexes: number[],
    landslideId: number,
    terrainGrid: TerrainGrid,
    is3x3: boolean
  ): Promise<PaintResponse.AsObject> {
    const request: PaintMultipleRequest = new PaintMultipleRequest();
    if (!terrainGrid.id) {
      throw Error('Terrain Grid does not have a value for `id` property.');
    }
    request.setSimulationid(terrainGrid.id);
    request.setIndexesList(indexes);
    request.setLandslideid(landslideId);
    request.setIs3x3(is3x3);
    let response: PaintResponse.AsObject;


  return this.requestHeaderService.getHeaderPromise().then((metadata) => {
      return new Promise((resolve, reject) => {
        this.inactivityService.resetInactivityTimer();
        grpc.invoke(TerrainPaintingService.PaintThemAll, {
          request: request,
          host: '',
          metadata: new grpc.Metadata(metadata),
          onMessage: (message: PaintResponse) => {
            response = message.toObject();
            this.updateTerrainGrid(response, terrainGrid);
          },
          onEnd: (
            code: grpc.Code,
            message: string,
            trailers: grpc.Metadata
          ): any => {
            if (code === grpc.Code.OK) {
              resolve(response); // Resolve the promise when the gRPC call is successful
            } else {
              console.error('An error occurred', code, message, trailers);
              reject(new Error(`gRPC error: ${code} - ${message}`));
            }
          },
        });
      });
    });
    
  }

  public paintCells(
    features: IGeoJSONFeatureCollection,
    terrainGrid: TerrainGrid
  ): Promise<PaintResponse.AsObject> {
    const request: PaintCellsRequest = new PaintCellsRequest();
    if (!terrainGrid.id) {
      throw Error('Terrain Grid does not have a value for `id` property.');
    }
    request.setSimulationid(terrainGrid.id);
    request.setFeaturecollection(JSON.stringify(features));
    request.setIs3x3(true);
    let response: PaintResponse.AsObject;

    return this.requestHeaderService.getHeaderPromise().then((metadata) => {
      return new Promise((resolve, reject) => {
        this.inactivityService.resetInactivityTimer();
        grpc.invoke(TerrainPaintingService.PaintCells, {
          request: request,
          host: '',
          metadata: new grpc.Metadata(metadata),
          onMessage: (message: PaintResponse) => {
            response = message.toObject();
            this.updateTerrainGrid(response, terrainGrid);
          },
          onEnd: (
            code: grpc.Code,
            message: string,
            trailers: grpc.Metadata
          ): void => {
            if (code === grpc.Code.OK) {
              resolve(response); // Resolve the promise when the gRPC call is successful
            } else {
              console.error('An error occurred', code, message, trailers);
              reject(new Error(`gRPC error: ${code} - ${message}`));
            }
          },
        });
      });
    });

    
  }

  public updateTerrainGrid(
    response: PaintResponse.AsObject,
    terrainGrid: TerrainGrid
  ) {
    for (const paintCell of response.paintcellsList) {
      if (!paintCell.location) {
        throw Error('location is missing');
      }
      const cellIndex: number = this.convertPointToIndex(
        paintCell.location,
        terrainGrid.cols
      );
      const cell: Cell = terrainGrid.cellsList[cellIndex];
      cell.color = paintCell.color;
    }
  }

  convertPointToIndex(location: Point.AsObject, colNums: number): number {
    return location.y * colNums + location.x;
  }
}
